/**
 * Helpers for embedding draw.io diagrams inside homework HTML.
 * Structure mirrors Confluence-style blocks: title + clickable preview.
 */

export type DrawioBlockValue = {
  diagramId: string
  pngUrl: string
  xmlUrl: string
  title?: string
  version?: number
}

export const DRAWIO_BLOCK_CLASS = 'hw-drawio'
export const DRAWIO_PREVIEW_CLASS = 'hw-drawio-preview'
export const DRAWIO_TITLE_CLASS = 'hw-drawio-title'

function escapeHtml(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function createDiagramId(): string {
  return `dg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

export function buildDrawioBlockHtml(value: DrawioBlockValue): string {
  const id = escapeHtml(value.diagramId)
  const png = escapeHtml(value.pngUrl)
  const xml = escapeHtml(value.xmlUrl)
  const title = escapeHtml(value.title?.trim() || 'Sơ đồ quy trình')
  const version = Number(value.version) > 0 ? Number(value.version) : 1
  return (
    `<div class="${DRAWIO_BLOCK_CLASS}" data-diagram-id="${id}" data-xml-url="${xml}" data-png-url="${png}" data-version="${version}" contenteditable="false">` +
    `<p class="${DRAWIO_TITLE_CLASS}"><strong>${title}</strong></p>` +
    `<img class="${DRAWIO_PREVIEW_CLASS}" src="${png}" alt="${title}" data-diagram-id="${id}" data-xml-url="${xml}" />` +
    `<p class="hw-drawio-hint"><em>Nhấn để chỉnh sửa sơ đồ (khi được phép sửa bài)</em></p>` +
    `</div>`
  )
}

export function parseDrawioBlock(el: Element): DrawioBlockValue | null {
  const node = el.closest(`.${DRAWIO_BLOCK_CLASS}`) || el
  if (!(node instanceof HTMLElement)) return null
  if (!node.classList.contains(DRAWIO_BLOCK_CLASS) && !node.classList.contains(DRAWIO_PREVIEW_CLASS)) {
    return null
  }
  const root =
    node.classList.contains(DRAWIO_BLOCK_CLASS)
      ? node
      : (node.closest(`.${DRAWIO_BLOCK_CLASS}`) as HTMLElement | null)
  if (!root) return null

  const img = root.querySelector(`img.${DRAWIO_PREVIEW_CLASS}`) as HTMLImageElement | null
  const diagramId =
    root.getAttribute('data-diagram-id') ||
    img?.getAttribute('data-diagram-id') ||
    ''
  const pngUrl =
    root.getAttribute('data-png-url') ||
    img?.getAttribute('src') ||
    img?.currentSrc ||
    ''
  const xmlUrl =
    root.getAttribute('data-xml-url') ||
    img?.getAttribute('data-xml-url') ||
    ''
  if (!diagramId || !pngUrl) return null

  const titleEl = root.querySelector(`.${DRAWIO_TITLE_CLASS}`)
  const title = (titleEl?.textContent || '').trim() || 'Sơ đồ quy trình'
  const version = Number(root.getAttribute('data-version') || '1') || 1

  return {
    diagramId,
    pngUrl,
    xmlUrl,
    title,
    version,
  }
}

/** Replace an existing drawio block (by diagramId) inside HTML string. */
export function replaceDrawioBlockInHtml(
  html: string,
  value: DrawioBlockValue
): { html: string; replaced: boolean } {
  if (typeof DOMParser === 'undefined') {
    return { html, replaced: false }
  }
  try {
    const doc = new DOMParser().parseFromString(
      `<div id="__hw_root">${html || ''}</div>`,
      'text/html'
    )
    const root = doc.getElementById('__hw_root')
    if (!root) return { html, replaced: false }

    const target = root.querySelector(
      `.${DRAWIO_BLOCK_CLASS}[data-diagram-id="${CSS.escape(value.diagramId)}"]`
    )
    if (!target) return { html, replaced: false }

    const wrap = doc.createElement('div')
    wrap.innerHTML = buildDrawioBlockHtml(value)
    const next = wrap.firstElementChild
    if (!next) return { html, replaced: false }
    target.replaceWith(next)
    return { html: root.innerHTML, replaced: true }
  } catch {
    return { html, replaced: false }
  }
}

/** Append a drawio block (+ optional blank line) to HTML. */
export function appendDrawioBlockToHtml(html: string, value: DrawioBlockValue): string {
  const block = buildDrawioBlockHtml(value)
  const base = String(html || '').trim()
  if (!base) return `<p><br></p>${block}<p><br></p>`
  return `${base}${block}<p><br></p>`
}
